import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  inject,
  input,
  model,
  viewChild,
  output,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { outputFromObservable } from '@angular/core/rxjs-interop';
import { AgGridModule } from 'ag-grid-angular';
import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  CellClickedEvent,
  CellValueChangedEvent,
  CheckboxSelectionCallbackParams,
  Column,
  ColumnApi,
  ColumnRowGroupChangedEvent,
  FirstDataRenderedEvent,
  GetGroupRowAggParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IAggFuncParams,
  ICellRendererParams,
  InitialGroupOrderComparatorParams,
  IRowNode,
  IServerSideGetRowsParams,
  ProcessCellForExportParams,
  RowDragMoveEvent,
  RowNode,
  RowSelectedEvent,
  ShouldRowBeSkippedParams,
} from 'ag-grid-community';
import { CheckboxSelectionCallback, ColDef, ColGroupDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { v4 as newGuid } from 'uuid';

import { AggFunc, ColourNames } from '../../../constants';
import { BaseComponent } from '../../base/base.component';
import { DeleteModalComponent } from '../../modals/delete-modal/delete-modal.component';
import {
  ColumnKeyMap,
  ColumnResized,
  GridChartCellClicked,
  GridFieldOptions,
  GridRowSelect,
  GridState,
  IdDescription,
  ScrollChanged,
  UserPreference,
} from '../../../models';
import {
  BaseImportService,
  FormatService,
  MenuService,
  ModalService,
  NotificationService,
  ThemeService,
  UserPreferenceService,
  UtilityService,
} from '../../../services';
import { FormlyService } from '../../../services/component-services/formly.service';
import { FormService } from '../../../services/component-services/form.service';
import { GridService } from '../../../services/component-services/grid.service';
import { ignoreModalError } from '../../../helpers';

@Component({
  selector: 'app-grid',
  templateUrl: './grid.component.html',
  styleUrl: './grid.component.scss',
  imports: [NgTemplateOutlet, AgGridModule],
})
export class GridComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {
  // The grid's own theme follows the chosen appearance; both themes ship in the balham stylesheet.
  protected readonly themeService = inject(ThemeService);
  private readonly modalService = inject(ModalService);
  private readonly gridService = inject(GridService);
  private readonly formService = inject(FormService);
  private readonly formatService = inject(FormatService);
  private readonly menuService = inject(MenuService);
  private readonly notificationService = inject(NotificationService);
  private readonly userPreferenceService = inject(UserPreferenceService);
  private readonly utilityService = inject(UtilityService);
  private readonly formlyService = inject(FormlyService);

  protected save$ = new Subject<void>();

  public readonly stateKey = model<string>();
  public readonly columnDefs = input<(ColDef | ColGroupDef)[]>([]);
  public readonly groupColumnHeader = input<string>();
  // Lets the user group the rows themselves: a "drag here" bar above the grid, a totals footer
  // under every group, and the text and date columns groupable (GridService marks the number,
  // checkbox and button columns as not). Opt-in: a grid that groups from its own column
  // definitions (Stock Reallocation, Stock Enquiry...) leaves it off and behaves as before.
  public readonly rowGrouping = input(false);
  public readonly groupDefaultExpanded = input(0);
  public readonly rememberGroupStateWhenNewData = input(false);
  public readonly header = input<string>();
  public readonly layoutStateKey = input<string>();
  public readonly filter = input(true);
  public readonly gridCssClass = input<string>();
  public readonly pagination = input(true);
  public readonly pageSize = input(100);
  public readonly resizable = input(true);
  public readonly rowData = model<any[]>([]);
  public readonly multiSelectRows = input(true);
  public readonly multiSelectCells = input(false);
  // Put the Columns / Filters bar UNDER the grid instead of in the header row. Opt-in, so
  // every existing grid keeps the layout it has: it is for screens whose header row is
  // already carrying action buttons, where the two collide.
  public readonly resetBelow = input(false);
  public readonly showDelete = input(true);
  public readonly showDeleteMessage = input(true);
  public readonly showDiscard = input(true);
  public readonly showPrint = input(false);
  public readonly showCumulativeTotals = input(false);
  public readonly showLayouts = input(false);
  public readonly retainState = input(false);
  public readonly sortable = input(true);
  public readonly sizeToFit = input(false);
  public readonly suppressScrollOnNewData = input(false);
  public readonly importService = input<BaseImportService>();
  public readonly chart = input(false);
  public readonly serverSide = input(false);
  public readonly gridIndex = input<number>();
  public readonly columnKeyMap = input<ColumnKeyMap>();
  public readonly delete = output<any>();
  public readonly print = output<any>();
  public readonly edit = output<any>();
  public readonly chartCellClicked = output<GridChartCellClicked>();
  public readonly rowSelected = output<GridRowSelect>();
  public readonly scrollChanged = output<ScrollChanged>();
  public readonly columnResized = output<ColumnResized>();
  public readonly firstDataRendered = output<FirstDataRenderedEvent>();
  public readonly save = outputFromObservable<void>(this.save$);
  public readonly imported = output<void>();
  public readonly savedState = output<void>();
  // Fires whenever the grid's sort / filter / column state may have changed: after a saved
  // layout is applied (on load and after new row data) and after every change the user makes.
  // savedState alone misses the first of those - it only fires once the state is loaded.
  public readonly stateChanged = output<void>();
  public readonly serverSideSearch = output<IServerSideGetRowsParams>();

  private readonly agGridRef = viewChild.required<ElementRef>('agGrid');

  public gridOptions: GridOptions;
  public edited = false;

  public gridApi: GridApi;
  public gridColumnApi: ColumnApi;
  protected state: GridState = {} as GridState;

  protected components: any;
  protected updateFilter = true;
  protected stateLoaded = false;
  protected filterCount = 0;

  public layoutsOptions$ = new BehaviorSubject<IdDescription[]>([]);
  private layouts$ = new BehaviorSubject<UserPreference[]>([]);
  private layoutFields: FormlyFieldConfig[];
  private layoutPreferenceKey?: string;
  private defaultState: GridState | null = null;
  private readonly globalText = $localize`Global`;

  private gridId: string;
  private modalLevel: number;
  private saveStateTimer: any;
  private rowGroupOpenedTimer: any;

  ngOnInit() {
    const columnDefs = [...this.columnDefs()] as ColDef[];

    if (this.showPrint()) {
      columnDefs.push(
        this.gridService.button(
          (model: any) => this.print.emit(model),
          '',
          'print',
          'text-dark',
          'fa fa-print',
          undefined,
          { pinned: 'right' },
        ),
      );
    }

    if (this.showDelete() && !this.menuService.isReadOnly) {
      columnDefs.push(
        this.gridService.button(
          (model: any) => {
            if (this.showDeleteMessage()) {
              if (model.canDelete !== false) {
                this.modalService
                  .openSmall(DeleteModalComponent)
                  .modal.result.then(() => this.delete.emit(model), ignoreModalError);
              }
            } else {
              this.delete.emit(model);
            }
          },
          '',
          'delete',
          'text-danger',
          'fa fa-trash-alt',
          (model: any) => model?.canDelete !== false,
          {
            showColumnFunc: (data: any[]) => data.some((x: any) => x.canDelete !== false),
            pinned: 'right',
          },
        ),
      );
    }

    if (this.serverSide()) {
      this.columnDefs().forEach((x: ColDef) => (x.filter = undefined));
    }

    const rowDrag = columnDefs.some((x: ColDef) => x.rowDrag);

    this.gridOptions = {
      columnDefs: columnDefs,
      defaultColDef: {
        filter: this.filter() && !this.serverSide(),
        resizable: this.resizable(),
        sortable: this.sortable() && !this.serverSide(),
        suppressMenu: !this.filter(),
      },
      autoGroupColumnDef: {
        headerName: this.groupColumnHeader() ?? columnDefs.find((x: ColDef) => x.rowGroup)?.headerName,
      },
      rowClassRules: {
        'bg-requires-review': (params) => params.data?.requiresReview === 'Yes',
        'bg-do-not-sell': (params) => params.data?.doNotSell === 'Yes',
        'bg-blue': (params) => params.data?.colour === ColourNames.blue,
        'bg-indigo': (params) => params.data?.colour === ColourNames.indigo,
        'bg-purple': (params) => params.data?.colour === ColourNames.purple,
        'bg-pink': (params) => params.data?.colour === ColourNames.pink,
        'bg-red': (params) => params.data?.colour === ColourNames.red,
        'bg-orange': (params) => params.data?.colour === ColourNames.orange,
        'bg-yellow': (params) => params.data?.colour === ColourNames.yellow,
        'bg-green': (params) => params.data?.colour === ColourNames.green,
        'bg-teal': (params) => params.data?.colour === ColourNames.teal,
        'bg-cyan': (params) => params.data?.colour === ColourNames.cyan,
        'bg-gray': (params) => params.data?.colour === ColourNames.gray,
        'bg-black': (params) => params.data?.colour === ColourNames.black,
      },
      pagination: this.pagination() && !rowDrag,
      paginationPageSize: this.pageSize(),
      rowDragManaged: rowDrag,
      suppressRowClickSelection: true,
      suppressRowTransform: true,
      suppressPropertyNamesCheck: true,
      suppressAggFuncInHeader: true,
      suppressMultiRangeSelection: true,
      stopEditingWhenCellsLoseFocus: true,
      suppressColumnVirtualisation: this.sizeToFit(),
      enableRangeSelection: this.multiSelectCells(),
      // Ctrl+C and the context menu's Copy take the focused cell, not every selected row.
      // updateCheckboxSelection() re-applies each row's selected flag, even with no checkbox column.
      suppressCopyRowsToClipboard: true,
    };

    if (this.rowGrouping()) {
      this.addRowGroupingOptions();
    }

    if (this.serverSide()) {
      this.gridOptions.rowModelType = 'serverSide';
      this.gridOptions.cacheBlockSize = this.pageSize();
      this.gridOptions.serverSideDatasource = {
        getRows: (params: IServerSideGetRowsParams) => this.serverSideSearch.emit(params),
      };
    }

    this.components = this.gridService.components;

    const checkboxRowSelect = columnDefs.find((x) => x.checkboxSelection);
    if (checkboxRowSelect) {
      this.gridOptions.rowSelection = this.multiSelectRows() ? 'multiple' : 'single';
      this.gridOptions.rowMultiSelectWithClick = true;
      // A group or footer row has no data, so it is never part of the selection.
      this.gridOptions.isRowSelectable = (rowNode: IRowNode) =>
        (!this.rowGrouping() || !!rowNode.data) &&
        (checkboxRowSelect.checkboxSelection as CheckboxSelectionCallback).call(this, {
          data: rowNode.data,
        } as CheckboxSelectionCallbackParams);
    }

    this.modalLevel = this.modalService.modalLevel;
    this.formService.save.pipe(this.untilDestroyed()).subscribe((modalLevel: number) => {
      if (modalLevel === this.modalLevel && this.edited) {
        this.save$.next();
      }
    });

    this.gridId = newGuid();
    this.formService.addGrid(this.modalLevel, this.gridId, this);
  }

  ngOnDestroy() {
    this.clearSaveStateTimer();
    clearTimeout(this.rowGroupOpenedTimer);
    this.formService.removeGrid(this.modalLevel, this.gridId);
    this.gridApi = undefined;
    this.gridColumnApi = undefined;
  }

  ngAfterViewInit() {
    const vViewport = this.agGridRef().nativeElement.querySelector('.ag-body-viewport') as HTMLElement;
    const hViewport = this.agGridRef().nativeElement.querySelector(
      '.ag-body-horizontal-scroll-viewport',
    ) as HTMLElement;

    if (vViewport) {
      vViewport.addEventListener('scroll', () => this.emitScrollChange());
    }

    if (hViewport) {
      hViewport.addEventListener('scroll', () => this.emitScrollChange());
    }
    this.gridOptions.onColumnResized = (event) => {
      if (this.columnKeyMap() && event.finished && event.column) {
        const colId = event.column.getColId();

        const logicalKey = Object.keys(this.columnKeyMap()).find((key) => this.columnKeyMap()[key] === colId);

        if (logicalKey) {
          const width = event.column.getActualWidth();
          this.columnResized.emit({ logicalKey, width });
        }
      }
    };
  }

  protected get showReset() {
    return this.filter() || this.sortable() || this.resizable();
  }

  protected get totalRowCount(): number {
    return Array.isArray(this.rowData()) ? this.rowData().length : 0;
  }

  protected get displayedRowCount(): number {
    // Grouped by the user, the displayed rows are group headings and footers, not lines.
    if (this.rowGrouping() && this.gridApi) {
      return this.count();
    }
    return this.gridApi?.getDisplayedRowCount() ?? this.totalRowCount;
  }

  protected get showRowCount(): boolean {
    return !this.serverSide() && this.totalRowCount > 0;
  }

  public get selectedIds(): string[] {
    return this.selectedRows.map((x) => x.id);
  }

  public get selectedRows(): any[] {
    const selectedRows = [];
    this.forEachRow((node) => {
      if (node.isSelected()) {
        selectedRows.push(node.data);
      }
    });
    return selectedRows;
  }

  public updateRow(model: any): boolean {
    let updated = false;
    this.forEachRow((rowNode: RowNode) => {
      if (rowNode.data && rowNode.data.id === model.id) {
        rowNode.setData(model);
        updated = true;
      }
    });

    return updated;
  }

  public get filteredRowData(): any[] {
    const filteredRowData = [];
    this.forEachRow((rowNode: RowNode) => {
      if (rowNode.data) {
        filteredRowData.push(rowNode.data);
      }
    });
    return filteredRowData;
  }

  public get sortedAndFilteredRowData(): any[] {
    const filteredRowData = [];
    this.forEachRowAfterFilterAndSort((rowNode: RowNode) => {
      if (rowNode.data) {
        filteredRowData.push(rowNode.data);
      }
    });
    return filteredRowData;
  }

  public get sortedAndFilteredRowIds(): string[] {
    const filteredRowIds = [];
    this.forEachRowAfterFilterAndSort((rowNode: RowNode) => {
      if (rowNode.data?.id) {
        filteredRowIds.push(rowNode.data.id);
      }
    });
    return filteredRowIds;
  }

  protected get initialRowData(): any[] {
    return this.serverSide() ? undefined : this.rowData();
  }

  protected get isEdited() {
    return this.edited;
  }

  private get userPreferenceKey() {
    return this.stateKey() ? `Grid.${this.stateKey()}` : '';
  }

  public resetEdited() {
    this.edited = false;
  }

  public export(fileName: string) {
    if (this.rowGrouping() && this.gridColumnApi?.getRowGroupColumns().length) {
      // Grouped, the file follows the screen: a closed group writes its heading and its footer,
      // an open one its lines as well. The totals are on the footer, so the heading carries
      // only the group's name.
      this.gridApi?.exportDataAsCsv({
        fileName,
        shouldRowBeSkipped: (params: ShouldRowBeSkippedParams) => this.insideClosedGroup(params.node),
        processCellCallback: (params: ProcessCellForExportParams) => this.groupedExportCell(params),
      });
    } else {
      this.gridApi?.exportDataAsCsv({ fileName });
    }
  }

  public loadState() {
    if (!this.stateKey()) {
      return;
    }

    this.updateFilter = false;
    this.userPreferenceService
      .get(this.userPreferenceKey)
      .pipe(this.untilDestroyed())
      .subscribe((userPreference: UserPreference) => {
        if (userPreference?.value) {
          this.state = JSON.parse(userPreference.value) as GridState;
        } else if (this.defaultState) {
          this.state = this.defaultState;
        } else {
          this.state = { columns: [], filter: undefined } as GridState;
        }
        this.updateFilter = true;
        this.applyState();
        this.stateLoaded = true;
      });
  }

  public applyState() {
    if (this.state) {
      // Read before the columns go in: regrouping rebuilds the groups, closed.
      const expandedGroups = this.state.expandedGroups;
      // With user grouping on, a column's total belongs to its definition, never to a saved
      // layout: one saved before grouping existed stores "aggFunc": null and would remove it.
      const columns = this.rowGrouping()
        ? this.state.columns?.map(({ aggFunc, ...column }) => column)
        : this.state.columns;
      this.gridColumnApi?.applyColumnState({ state: columns, applyOrder: true });
      this.gridApi?.setFilterModel(this.state.filter);
      if (this.rowGrouping()) {
        this.restoreExpandedGroups(expandedGroups ?? []);
      }
      this.updateFilterCount();
      this.stateChanged.emit();
    }
  }

  /** Any column sorted, or any column filter set - the grid is not in its natural order. */
  public get sortOrFilterApplied(): boolean {
    const sorted = (this.gridColumnApi?.getColumnState() ?? []).some((column) => !!column.sort);
    const filterModel = this.gridApi?.getFilterModel();
    return sorted || (!!filterModel && Object.keys(filterModel).length > 0);
  }

  private updateFilterCount() {
    const model = this.gridApi?.getFilterModel();
    this.filterCount = model ? Object.keys(model).length : 0;
  }

  public setFilter(filter: any) {
    const existingFilter = this.gridApi?.getFilterModel();
    this.gridApi?.setFilterModel({ ...existingFilter, ...filter });
  }

  public calculateAggregates() {
    if (this.columnDefs().some((x: ColDef) => x.aggFunc)) {
      const data = this.calculatePinnedBottomData();
      const rows = Object.keys(data).some((key) => key !== 'isAggregate' && data[key]) ? [data] : [];
      if (rows && this.showCumulativeTotals()) {
        const cumulativeData = this.calculateCumulativeData(data);
        rows.push(cumulativeData);
      }
      this.gridApi?.setPinnedBottomRowData(rows);
    }
  }

  public import(file: any) {
    this.importService()
      .import(file)
      .pipe(this.untilDestroyed())
      .subscribe({
        next: () => {
          this.imported.emit();
          this.notificationService.success($localize`Import completed successfully`);
        },
        error: (error: HttpErrorResponse) => this.notificationService.fail(error.error),
      });
  }

  public exportFile() {
    this.importService().export().pipe(this.untilDestroyed()).subscribe();
  }

  public exportTemplateAndDataFile() {
    this.importService().exportTemplateAndData().pipe(this.untilDestroyed()).subscribe();
  }

  public forEachRow(callback: (rowNode: RowNode) => void) {
    if (this.serverSide()) {
      this.gridApi?.forEachNode((rowNode: RowNode) => {
        if (rowNode.data) {
          callback(rowNode);
        }
      });
    } else {
      this.gridApi?.forEachNodeAfterFilter((rowNode: RowNode) => callback(rowNode));
    }
  }

  public refreshCells(nodes: IRowNode | IRowNode[], force = true) {
    this.gridApi.refreshCells({ rowNodes: Array.isArray(nodes) ? nodes : [nodes], force });
  }

  private forEachRowAfterFilterAndSort(callback: (rowNode: RowNode) => void) {
    if (this.serverSide()) {
      this.gridApi?.forEachNode((rowNode: RowNode) => {
        if (rowNode.data) {
          callback(rowNode);
        }
      });
    } else {
      this.gridApi?.forEachNodeAfterFilterAndSort((rowNode: RowNode) => callback(rowNode));
    }
  }

  protected clearSaveStateTimer() {
    if (this.saveStateTimer) {
      clearTimeout(this.saveStateTimer);
      this.saveStateTimer = undefined;
    }
  }

  protected onCellClicked(event: CellClickedEvent) {
    // A group heading or footer has no data behind it, so there is nothing to open.
    if (event.colDef.onCellClicked || (this.rowGrouping() && !event.data)) {
      return;
    }

    if (this.chart()) {
      this.chartCellClicked.emit({
        rowIndex: event.rowIndex,
        colIndex: this.getColumnIndex(event),
      });
    } else {
      if (event.data.canEdit !== false && !event.colDef.editable) {
        this.edit.emit(event.data);
      }
    }
  }

  protected saveState(updateFilterState = false) {
    if (!this.stateKey() || !this.gridColumnApi || !this.gridApi || !this.stateLoaded) {
      return;
    }

    this.clearSaveStateTimer();
    this.saveStateTimer = setTimeout(() => {
      const previousState = JSON.stringify(this.state);
      const newColumns = this.gridColumnApi?.getColumnState() ?? [];
      const newColIds = new Set(newColumns.map((c) => c.colId));
      const previousColumns = this.state.columns ?? [];
      const merged = [...newColumns];
      let insertAfter = -1;
      for (const prevCol of previousColumns) {
        if (newColIds.has(prevCol.colId)) {
          insertAfter = merged.findIndex((c) => c.colId === prevCol.colId);
        } else {
          merged.splice(insertAfter + 1, 0, prevCol);
          insertAfter++;
        }
      }
      this.state.columns = merged;
      if (updateFilterState) {
        this.state.filter = this.gridApi?.getFilterModel();
      }
      const newState = JSON.stringify(this.state);
      if (newState !== previousState && (!this.showLayouts() || this.retainState())) {
        this.userPreferenceService.set(this.userPreferenceKey, newState).subscribe();
      }

      if (this.showLayouts()) {
        this.loadLayouts().subscribe();
      }

      this.calculateAggregates();
      this.savedState.emit();
      this.stateChanged.emit();
    }, 100);
  }

  // A grid that groups from its own column definitions keeps saving only what it saved before.
  protected onColumnRowGroupChanged(event: ColumnRowGroupChangedEvent) {
    if (this.rowGrouping()) {
      // When the USER regroups, the state takes the groups as they now stand, so the old open ones
      // do not come back on the next search. A layout being applied ('api') brings its own open
      // groups, which may still be waiting for the rows.
      if (event.source !== 'api') {
        this.state.expandedGroups = this.openGroupRoutes();
      }
      this.saveState();
    }
  }

  // The open groups are part of the state, so a saved layout and a fresh search keep them. They
  // are remembered between visits only where a column move is (see saveState). Bound to both
  // rowGroupOpened and expandOrCollapseAll (the column menu's Expand All / Collapse All, which
  // fires no rowGroupOpened). A restore fires one event per group, so the reading is coalesced.
  protected onRowGroupOpened() {
    if (!this.rowGrouping() || this.rowGroupOpenedTimer) {
      return;
    }

    this.rowGroupOpenedTimer = setTimeout(() => {
      this.rowGroupOpenedTimer = undefined;
      if (!this.gridApi) {
        return;
      }

      this.state.expandedGroups = this.currentExpandedGroups();
      if (!this.showLayouts() || this.retainState()) {
        this.saveState();
      }
    });
  }

  // The groups open now, plus any saved open group this search does not contain, so a layout used
  // on a narrower date range keeps the groups it cannot show.
  private currentExpandedGroups(): (string | null)[][] {
    if (!this.gridColumnApi?.getRowGroupColumns().length) {
      return [];
    }

    const present = new Set<string>();
    this.gridApi?.forEachNode((node: IRowNode) => {
      if (node.group) {
        present.add(JSON.stringify(this.groupRoute(node)));
      }
    });
    const absent = (this.state.expandedGroups ?? []).filter((route) => !present.has(JSON.stringify(route)));
    return [...this.openGroupRoutes(), ...absent];
  }

  private openGroupRoutes(): (string | null)[][] {
    const routes: (string | null)[][] = [];
    this.gridApi?.forEachNode((node: IRowNode) => {
      if (node.group && !node.footer && node.expanded) {
        routes.push(this.groupRoute(node));
      }
    });
    return routes;
  }

  // AG Grid's own getRoute() stops at a blank key, so it cannot name a group under "(Blanks)".
  private groupRoute(node: IRowNode): (string | null)[] {
    const route: (string | null)[] = [];
    for (let pointer = node; pointer && pointer.level >= 0; pointer = pointer.parent) {
      route.unshift(pointer.key ?? null);
    }
    return route;
  }

  // Opens exactly the saved groups and closes the rest. A saved group the data no longer has is
  // skipped, and stays in the list. ⚠️ So a rowGrouping grid cannot also use groupDefaultExpanded:
  // the restore would close the groups it opens.
  private restoreExpandedGroups(expandedGroups: (string | null)[][]) {
    const open = new Set(expandedGroups.map((route) => JSON.stringify(route)));
    this.gridApi?.forEachNode((node: IRowNode) => {
      if (node.group) {
        node.setExpanded(open.has(JSON.stringify(this.groupRoute(node))));
      }
    });
    this.state.expandedGroups = expandedGroups;
  }

  protected onRowDragMove(event: RowDragMoveEvent) {
    this.setEdited();
  }

  protected onRowSelected(event: RowSelectedEvent) {
    if (!event.data || event.node.isSelected() === event.data.selected) {
      return;
    }
    this.setEdited();
    event.data.selected = event.node.isSelected();
    this.refreshCells(event.node);

    setTimeout(
      () =>
        this.rowSelected.emit({
          model: event.data,
          selected: event.node.isSelected(),
          rowIndex: event.rowIndex,
          node: event.node,
        }),
      event.rowIndex * 10,
    );
  }

  protected onFirstDataRendered(params: FirstDataRenderedEvent<any>) {
    setTimeout(() => this.firstDataRendered.emit(params));
  }

  protected gridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;

    if (!this.defaultState && this.gridColumnApi && this.gridApi) {
      this.defaultState = {
        columns: this.gridColumnApi.getColumnState(),
        filter: this.gridApi.getFilterModel(),
      } as GridState;
    }

    setTimeout(() => this.loadState(), 100);
  }

  protected cellValueChanged(event: CellValueChangedEvent) {
    this.setEdited();
    this.calculateAggregates();
  }

  protected rowDataUpdated() {
    setTimeout(() => {
      this.hideColumns();
      this.calculateAggregates();
      this.updateCheckboxSelection();
      this.applyState();
    }, 0);
  }

  protected filterChanged() {
    this.hideColumns();
    this.saveState(this.updateFilter);
    this.updateFilterCount();
    this.calculateAggregates();
  }

  protected resetColumns() {
    this.gridColumnApi?.resetColumnState();
  }

  protected resetFilters() {
    this.gridApi?.setFilterModel(undefined);
  }

  private getColumnIndex(event: CellClickedEvent): number {
    const field = event.colDef.field;
    return event.columnApi.getColumns()?.findIndex((col) => col.getColDef().field === field);
  }

  private setEdited() {
    if (this.save$.observed && this.showDiscard()) {
      this.edited = true;
    }
  }

  private hideColumns() {
    if (!this.columnDefs().some((col: ColDef | any) => col.showColumnFunc)) {
      return;
    }

    this.columnDefs().forEach((col: ColDef | any) => {
      if (col.showColumnFunc) {
        this.gridColumnApi?.setColumnVisible(col.field, col.showColumnFunc(this.filteredRowData));
      }
    });

    this.state.columns = this.gridColumnApi?.getColumnState();
  }

  private calculatePinnedBottomData() {
    const data = {
      isAggregate: true,
    };

    this.columnDefs().forEach((col: ColDef | any) => {
      if (col.showAggFunc && !col.showAggFunc(this.filteredRowData)) {
        return;
      }

      const field = col.field;
      if (col.customAggFunc) {
        data[field] = col.customAggFunc(this.filteredRowData);
        return;
      }

      switch (col.aggFunc) {
        case AggFunc.avg:
          data[field] = this.formatService.number(this.average(col, field, false));
          break;
        case AggFunc.count:
          data[field] = this.formatService.number(this.count(), 0);
          break;
        case AggFunc.first:
          break;
        case AggFunc.sum:
          const sum = this.sum(col, field, false);
          if (this.showCumulativeTotals()) {
            data[`${field}Sum`] = sum;
          }
          data[field] = this.formatService.number(sum);
          break;
        case AggFunc.sumInteger:
          const sumInt = this.sum(col, field, false);
          if (this.showCumulativeTotals()) {
            data[`${field}Sum`] = sumInt;
          }
          data[field] = this.formatService.number(sumInt, 0);
          break;
      }

      switch (col.groupAggFunc) {
        case AggFunc.avg:
          data[field] = this.formatService.number(this.average(col, field, true), 0);
          break;
        case AggFunc.count:
          data[field] = this.formatService.number(this.count(), 0);
          break;
        case AggFunc.first:
          break;
        case AggFunc.sum:
          data[field] = this.formatService.number(this.sum(col, field, true));
          break;
        case AggFunc.sumInteger:
          data[field] = this.formatService.number(this.sum(col, field, true), 0);
          break;
      }
    });

    return data;
  }

  private calculateCumulativeData(data: any) {
    const cumulativeData = {
      isAggregate: true,
    };

    let total = 0;
    this.columnDefs().forEach((col: ColDef | any) => {
      const num = data[`${col.field}Sum`];
      if (!isNaN(num)) {
        if (!col.ignoreCumulativeColumn) {
          total += num;
        }
        cumulativeData[col.field] = this.formatService.number(total);
      }
    });

    return cumulativeData;
  }

  private count(): number {
    let count = 0;
    // Grouped by the user, the walk also meets the group headings, which are not rows of the result.
    this.forEachRow((rowNode: RowNode) => {
      if (!this.rowGrouping() || rowNode.data) {
        count++;
      }
    });
    return count;
  }

  private sum(col: any, field: any, group: boolean): number {
    const calculation = col.calculation;
    const groupField = group ? this.findRowGroupField() : undefined;
    const seenKeys = groupField ? new Set<any>() : undefined;
    let sum = 0;

    const accumulate = (data: any) => {
      if (!data) {
        return;
      }
      if (groupField && seenKeys) {
        const key = data[groupField];
        if (seenKeys.has(key)) {
          return;
        }
        seenKeys.add(key);
      }
      const value = calculation ? calculation(data) : data[field];
      if (value) {
        sum += this.formatService.parseNumberString(value);
      }
    };

    this.forEachRow((rowNode: RowNode) => accumulate(rowNode.data));
    return sum;
  }

  private findRowGroupField(): string | undefined {
    const groupCol = (this.columnDefs() ?? []).find((c: any) => c.rowGroup) as any;
    return groupCol?.field;
  }

  private average(col: any, field: any, group: boolean): number {
    const sum = this.sum(col, field, group);
    return this.count() ? sum / this.count() : 0;
  }

  private addRowGroupingOptions() {
    const aggregate = (params: IAggFuncParams) => this.groupAggregate(params);
    this.gridOptions.rowGroupPanelShow = 'always';
    this.gridOptions.groupIncludeFooter = true;
    this.gridOptions.context = { ...this.gridOptions.context, rowGrouping: true };
    this.gridOptions.defaultColDef = { ...this.gridOptions.defaultColDef, enableRowGroup: true };
    // The grid's own sum would add the visible children; these read the group's lines, so a
    // footer obeys the same rules as the totals row (showAggFunc, customAggFunc). getGroupRowAgg
    // below fills every group row and the grid then never calls these; they stay registered so a
    // column's aggFunc (AggFunc.custom included) is always a name the grid knows.
    this.gridOptions.aggFuncs = {
      [AggFunc.sum]: aggregate,
      [AggFunc.sumInteger]: aggregate,
      [AggFunc.count]: aggregate,
      [AggFunc.avg]: aggregate,
      [AggFunc.custom]: aggregate,
    };
    this.gridOptions.getGroupRowAgg = (params: GetGroupRowAggParams) => this.groupRowValues(params);
    this.gridOptions.initialGroupOrderComparator = (params: InitialGroupOrderComparatorParams) =>
      this.compareGroups(params);
    this.gridOptions.autoGroupColumnDef = {
      ...this.gridOptions.autoGroupColumnDef,
      headerName: this.gridOptions.autoGroupColumnDef?.headerName ?? $localize`Group`,
      // The grouped column's own renderer would draw the footer as the bare group name, so the
      // group column draws its name - and "Total" before it on a footer - itself.
      cellRendererParams: {
        innerRenderer: (params: ICellRendererParams) => {
          // The pinned totals row is not a group: it has no name, blank or otherwise.
          if (!params.node?.group) {
            return params.value ? this.formatService.escapeHtml(params.value) : '';
          }
          const name = params.value ? this.formatService.escapeHtml(params.value) : $localize`(Blanks)`;
          return params.node?.footer ? $localize`Total ${name}` : name;
        },
      },
    };
    this.gridOptions.localeText = {
      rowGroupColumnsEmptyMessage: $localize`Drag here to set row groups`,
      groups: $localize`Row Groups`,
      group: $localize`Group`,
      groupBy: $localize`Group by`,
      ungroupBy: $localize`Un-Group by`,
      expandAll: $localize`Expand All Row Groups`,
      collapseAll: $localize`Collapse All Row Groups`,
    };
  }

  // A group row (heading while closed, and footer) carries its totals and, in every text or date
  // column, the value its lines share - the customer name on a customer, the product on a batch -
  // so the group reads without opening it. Lines that differ leave the cell blank. Number, rate,
  // tick-box and button columns (not groupable) show only a total. This replaces the grid's own
  // aggregation for every column, and sorting a text column now orders the groups by these values.
  private groupRowValues(params: GetGroupRowAggParams): Record<string, any> {
    const values: Record<string, any> = {};
    const rowNode = params.nodes[0]?.parent;
    if (!rowNode) {
      return values;
    }

    // Walked once per group and shared by every column, not once per column.
    const leaves = this.groupLeafNodes(rowNode);
    const lines = leaves.map((node: IRowNode) => node.data);
    (params.columnApi.getColumns() ?? []).forEach((column: Column) => {
      const colDef = column.getColDef();
      if (column.isValueActive()) {
        values[column.getColId()] = this.groupAggregate({ colDef, rowNode } as IAggFuncParams, lines);
      } else if (colDef.enableRowGroup !== false && !column.isRowGroupActive()) {
        const shared = this.sharedValue(column, leaves, params.api);
        if (shared !== undefined) {
          values[column.getColId()] = shared;
        }
      }
    });
    return values;
  }

  private sharedValue(column: Column, leaves: IRowNode[], api: GridApi): any {
    let shared: any;
    for (let index = 0; index < leaves.length; index++) {
      const value = api.getValue(column, leaves[index]) ?? '';
      if (index === 0) {
        shared = value;
      } else if (value !== shared) {
        return undefined;
      }
    }
    return shared === '' ? undefined : shared;
  }

  private groupAggregate(params: IAggFuncParams, groupLines?: any[]): number | null {
    const col = params.colDef as ColDef & GridFieldOptions;
    const lines = groupLines ?? this.groupLines(params.rowNode);
    // showAggFunc (one product type, one currency) is judged on the group's own lines: a batch is
    // one product, so it is totalled even when the whole result mixes kilos and metres.
    if (col.showAggFunc && !col.showAggFunc(lines)) {
      return null;
    }

    if (col.customAggFunc) {
      return this.formatService.parseNumberString(col.customAggFunc(lines));
    }

    switch (col.aggFunc) {
      case AggFunc.count:
        return lines.length;
      case AggFunc.avg:
        return lines.length ? this.sumOf(col, lines) / lines.length : null;
      // getGroupRowAgg replaces the grid's own first/last/min/max, so they are kept here.
      case AggFunc.first:
        return lines.length ? this.lineNumber(col, lines[0]) : null;
      case AggFunc.last:
        return lines.length ? this.lineNumber(col, lines[lines.length - 1]) : null;
      case AggFunc.min:
      case AggFunc.max: {
        // As the grid's own min and max do, a line with no value is left out rather than read as 0.
        const numbers = lines
          .filter((data: any) => {
            const value = this.lineValue(col, data);
            return value !== null && value !== undefined && value !== '';
          })
          .map((data: any) => this.lineNumber(col, data));
        if (!numbers.length) {
          return null;
        }
        return col.aggFunc === AggFunc.min ? Math.min(...numbers) : Math.max(...numbers);
      }
      default:
        return this.sumOf(col, lines);
    }
  }

  private sumOf(col: any, lines: any[]): number {
    const sum = lines.reduce((total: number, data: any) => total + this.lineNumber(col, data), 0);
    // A number() column shows its value unformatted, so drop the binary noise (0.1 + 0.2).
    return this.formatService.roundNumber(sum, 6);
  }

  private lineNumber(col: any, data: any): number {
    const value = this.lineValue(col, data);
    return value ? this.formatService.parseNumberString(value) : 0;
  }

  private lineValue(col: any, data: any): any {
    return col.calculation ? col.calculation(data) : data[col.field];
  }

  /** The filtered lines under a group, at any depth. */
  private groupLines(rowNode: IRowNode): any[] {
    return this.groupLeafNodes(rowNode).map((node: IRowNode) => node.data);
  }

  private groupLeafNodes(rowNode: IRowNode): IRowNode[] {
    const leaves: IRowNode[] = [];
    const walk = (node: IRowNode) => {
      if (node.data) {
        leaves.push(node);
      }
      (node.childrenAfterFilter ?? []).forEach((child: IRowNode) => walk(child));
    };
    walk(rowNode);
    return leaves;
  }

  // Groups open in their own order - a date by the date, a number-like key (a batch) by its
  // value - with the blank group last, until the user sorts the group column.
  private compareGroups(params: InitialGroupOrderComparatorParams): number {
    const keyA = params.nodeA.key ?? '';
    const keyB = params.nodeB.key ?? '';
    if (!keyA || !keyB) {
      return keyA === keyB ? 0 : keyA ? -1 : 1;
    }

    // A date column's key is the formatted date. Its filter comparator reads (filter, cell) and
    // is negative when the cell is the earlier date, so the keys go in swapped.
    const colDef = params.nodeA.rowGroupColumn?.getColDef();
    const dateComparator = colDef?.filter === 'agDateColumnFilter' ? colDef.filterParams?.comparator : undefined;
    return dateComparator ? dateComparator(keyB, keyA) : keyA.localeCompare(keyB, undefined, { numeric: true });
  }

  private insideClosedGroup(node: IRowNode): boolean {
    for (let parent = node.parent; parent && parent.level >= 0; parent = parent.parent) {
      if (!parent.expanded) {
        return true;
      }
    }
    return false;
  }

  private groupedExportCell(params: ProcessCellForExportParams): any {
    const node = params.node;
    if (!node?.group) {
      return params.value;
    }

    if (params.column.getColDef().showRowGroup) {
      return node.footer ? $localize`Total ${node.key ?? ''}` : (node.key ?? '');
    }

    return node.footer ? params.value : '';
  }

  public updateCheckboxSelection() {
    setTimeout(() => this.forEachRow((rowNode: RowNode) => rowNode.setSelected(rowNode.data?.selected ?? false)), 0);
  }

  public clearCheckboxSelection(index: number) {
    this.forEachRow((rowNode: RowNode) => {
      if (rowNode.data && rowNode.rowIndex === index) {
        rowNode.data.selected = false;
      }
    });
    this.updateCheckboxSelection();
  }

  public scrollToRatio(scrollChanged: ScrollChanged) {
    const vViewport = this.agGridRef().nativeElement.querySelector('.ag-body-viewport') as HTMLElement;
    const hViewport = this.agGridRef().nativeElement.querySelector(
      '.ag-body-horizontal-scroll-viewport',
    ) as HTMLElement;

    if (vViewport) {
      const maxScrollTop = vViewport.scrollHeight - vViewport.clientHeight;
      vViewport.scrollTop = scrollChanged.top * maxScrollTop;
    }

    if (hViewport) {
      const maxScrollLeft = hViewport.scrollWidth - hViewport.clientWidth;
      hViewport.scrollLeft = scrollChanged.left * maxScrollLeft;
    }
  }

  emitScrollChange() {
    const vViewport = this.agGridRef().nativeElement.querySelector('.ag-body-viewport') as HTMLElement;
    const hViewport = this.agGridRef().nativeElement.querySelector(
      '.ag-body-horizontal-scroll-viewport',
    ) as HTMLElement;

    const topRatio = vViewport ? vViewport.scrollTop / (vViewport.scrollHeight - vViewport.clientHeight || 1) : 0;

    const leftRatio = hViewport ? hViewport.scrollLeft / (hViewport.scrollWidth - hViewport.clientWidth || 1) : 0;

    this.scrollChanged.emit({ top: topRatio, left: leftRatio });
  }

  public setColumnWidth(colId: string, width: number) {
    if (this.gridApi && this.gridApi.getColumnDef(colId)) {
      this.gridColumnApi.setColumnWidth(colId, width, false); // false = no shift of other columns
    }
  }

  public loadLayouts() {
    return this.userPreferenceService.search(this.layoutUserPreferenceKey).pipe(
      this.untilDestroyed(),
      tap((userPreferences: UserPreference[]) => {
        this.layouts$.next(userPreferences);
        this.layoutsOptions$.next(
          this.utilityService.sort(
            userPreferences.map(
              (userPreference: UserPreference): IdDescription => ({
                id: userPreference.id,
                description: userPreference.key.includes(this.layoutUserPreferenceKeyGlobal)
                  ? `${userPreference.key.replace(this.layoutUserPreferenceKeyGlobal, '')} (${this.globalText})`
                  : userPreference.key.replace(this.layoutUserPreferenceKey, ''),
              }),
            ),
            (option: IdDescription) => option.description,
          ),
        );
      }),
    );
  }

  public get layouts(): UserPreference[] {
    return this.layouts$.value;
  }

  public saveCurrentStateAsLayout(global: boolean, name: string): Observable<string> {
    const key = `${global ? this.layoutUserPreferenceKeyGlobal : this.layoutUserPreferenceKey}${name}`;

    this.captureExpandedGroups();
    return this.userPreferenceService.set(key, JSON.stringify(this.state)).pipe(
      this.untilDestroyed(),
      tap(() => this.loadLayouts().subscribe()),
      map((savedPreference: UserPreference) => savedPreference.id),
    );
  }

  public updateCurrentLayout(): Observable<any> {
    const layoutKey = this.layoutPreferenceKey ?? this.layoutStateKey();
    if (!layoutKey) {
      return undefined;
    }

    this.captureExpandedGroups();
    return this.userPreferenceService.set(layoutKey, JSON.stringify(this.state)).pipe(
      this.untilDestroyed(),
      tap(() => this.loadLayouts().subscribe()),
    );
  }

  // Read from the grid at the moment of saving: the rowGroupOpened event arrives asynchronously.
  // Choosing a layout saves it straight back (grid-layout-form.service), often before Show
  // Details, so with no rows yet the layout's own open groups are kept rather than wiped.
  private captureExpandedGroups() {
    if (!this.rowGrouping() || !this.gridApi) {
      return;
    }

    let hasRows = false;
    this.gridApi.forEachNode(() => (hasRows = true));
    if (hasRows) {
      this.state.expandedGroups = this.currentExpandedGroups();
    }
  }

  public deleteLayout(layoutId: string) {
    return this.userPreferenceService.delete(layoutId).pipe(
      this.untilDestroyed(),
      tap(() => {
        this.layoutPreferenceKey = undefined;
        this.loadLayouts().subscribe();
      }),
    );
  }

  public canDeleteLayout(layoutId: string): boolean {
    const preference = this.layouts$.value.find((userPreference: UserPreference) => userPreference.id === layoutId);
    return !!preference && preference.canDelete !== false;
  }

  public applyLayout(layoutId?: string) {
    if (layoutId) {
      const layout = this.layouts$.value.find((x) => x.id === layoutId);
      if (!layout) {
        return;
      }

      this.layoutPreferenceKey = layout.key;
      this.state = JSON.parse(layout.value) as GridState;
      this.applyState();
    } else {
      this.loadState();
    }
  }
  public getLayoutFields(): FormlyFieldConfig[] {
    if (!this.layoutFields) {
      this.layoutFields = [
        this.formlyService.text('name', $localize`Layout Name`, { required: true, maxLength: 100 }),
        this.formlyService.checkbox('global', $localize`Global to all users`),
      ];
    }
    return this.layoutFields;
  }

  private get layoutUserPreferenceKey(): string {
    return this.stateKey() ? `Layout.${this.stateKey()}.` : '';
  }

  private get layoutUserPreferenceKeyGlobal(): string {
    return `${this.layoutUserPreferenceKey}Global.`;
  }
}
